<script setup lang="ts">
import { computed } from 'vue'
import { trans } from '@/munio/i18n'
import { numberFormat, pendoLabel as pendo } from '@/munio/utils'
import { Meta } from '@/munio/api/data'
import MdlButton from '@component/mdl/Button.vue'
import MdlProgressBar from '@component/mdl/ProgressBar.vue'

defineOptions({ name: 'MdlPaginator', inheritAttrs: false })

type PaginationValue = Partial<Meta> & {
  perPage?: number
  currentPage?: number
  totalPages?: number
  from?: number
  to?: number
  total?: number
  count?: number
}

const props = withDefaults(
  defineProps<{
    value?: PaginationValue
    count?: number
    rowsFrom?: number
    rowsTo?: number
    rowsTotal?: number
    pageRows?: number
    pageCurrent?: number
    pageLast?: number
    loading?: boolean | 'page' | 'pagination'
  }>(),
  {
    count: 0,
    rowsFrom: 0,
    rowsTo: 0,
    rowsTotal: 0,
    pageRows: 25,
    pageCurrent: 1,
    pageLast: 1,
    loading: false,
  },
)

const emit = defineEmits<{
  (e: 'goto', page: number): void
  (e: 'paginate', page: number): void
  (e: 'change', page: number): void
}>()

const pagination = computed(() => {
  const pageRows =
    props.value?.per_page ?? props.value?.perPage ?? props.pageRows
  const pageCurrent =
    props.value?.current_page ?? props.value?.currentPage ?? props.pageCurrent
  const pageLast =
    props.value?.last_page ?? props.value?.totalPages ?? props.pageLast
  const count = props.value?.count ?? 0
  const offset = (pageCurrent - 1) * count
  const rowsFrom = props.value?.from || props.rowsFrom || offset + 1
  const rowsTo = props.value?.to || props.rowsTo || offset + count
  const rowsTotal = props.value?.total ?? props.rowsTotal

  return {
    rowsFrom: rowsFrom,
    rowsTo: rowsTo,
    rowsTotal: rowsTotal,
    pageRows: pageRows,
    pageCurrent: pageCurrent,
    pageLast: pageLast,
  }
})

const cannotClickPrev = computed(() => {
  return !!props.loading || pagination.value.pageCurrent <= 1
})

const cannotClickNext = computed(() => {
  return (
    !!props.loading || pagination.value.pageCurrent >= pagination.value.pageLast
  )
})

const rowsText = computed(() => {
  const total = numberFormat(pagination.value.rowsTotal)
  const num =
    pagination.value.pageLast === 1
      ? `${numberFormat(pagination.value.rowsTo)}`
      : `${numberFormat(pagination.value.rowsFrom)}-${numberFormat(pagination.value.rowsTo)}`

  return trans(':num of :total', { num, total })
})

function paginate(page: number) {
  emit('paginate', page)
  emit('change', page)
  emit('goto', page)
}
</script>

<template>
  <div class="mdl-paginator-container">
    <nav
      v-if="typeof pagination.rowsFrom === 'number'"
      class="mdl-paginator"
      :class="$attrs.class"
    >
      <div class="mdl-paginator__rows">{{ rowsText }}</div>
      <div class="mdl-paginator__actions">
        <MdlButton
          icon
          primary
          rel="prev"
          :disabled="cannotClickPrev"
          :data-qa-id="pendo('paginatorFirst')"
          @click="paginate(1)"
          class="mdl-paginator__button-first"
        >
          <i class="material-icons">first_page</i>
        </MdlButton>
        <MdlButton
          icon
          primary
          rel="prev"
          :disabled="cannotClickPrev"
          :data-qa-id="pendo('paginatorPrevious')"
          @click="paginate(pagination.pageCurrent - 1)"
          class="mdl-paginator__button-prev"
        >
          <i class="material-icons">navigate_before</i>
        </MdlButton>
        <MdlButton
          icon
          primary
          rel="next"
          :disabled="cannotClickNext"
          :data-qa-id="pendo('paginatorNext')"
          @click="paginate(pagination.pageCurrent + 1)"
          class="mdl-paginator__button-next"
        >
          <i class="material-icons">navigate_next</i>
        </MdlButton>
        <MdlButton
          icon
          primary
          rel="last"
          :disabled="cannotClickNext"
          :data-qa-id="pendo('paginatorLast')"
          @click="paginate(pagination.pageLast)"
          class="mdl-paginator__button-last"
        >
          <i class="material-icons">last_page</i>
        </MdlButton>
      </div>
      <MdlProgressBar v-if="loading" indeterminate />
    </nav>
  </div>
</template>
